import React, { useState, useRef, useMemo, useCallback } from 'react';
import { Card, Select, DatePicker, Button, Radio, Checkbox, Space, message, Spin, Empty, Segmented, Tag, Collapse, Switch, ColorPicker, Input, Tooltip, Progress } from 'antd';
import { CameraOutlined, DownloadOutlined, ReloadOutlined, UnorderedListOutlined, PictureOutlined, StarOutlined, StarFilled, AppstoreOutlined, SettingOutlined, FontSizeOutlined, BgColorsOutlined, LeftOutlined, RightOutlined, FileImageOutlined, VideoCameraOutlined } from '@ant-design/icons';
import moment from 'moment';
import { toPng } from 'html-to-image';
import { Muxer, ArrayBufferTarget } from 'mp4-muxer';
import { fetchEventsForCalendar } from '../../server/publications/publicationServer';
import { getActiveCountries } from '../../server/configuration/FiltersServer';
import logoSway from '../../assets/images/logo/logosway.png';

const { Option } = Select;
const { RangePicker } = DatePicker;

// ── Theme definitions ──
const THEMES = {
  dark: {
    label: 'Dark', swatch: '#111',
    bg: 'linear-gradient(170deg, #000000 0%, #0a0a0a 25%, #111 55%, #0a0a0a 100%)',
    text: '#fff', textMuted: 'rgba(255,255,255,0.55)', textSoft: 'rgba(255,255,255,0.4)', textFaint: 'rgba(255,255,255,0.3)',
    accent: '#a855f7',
    orbA: 'rgba(255,255,255,0.04)', orbB: 'rgba(255,255,255,0.03)',
    cardBg: 'rgba(255,255,255,0.03)', cardBorder: 'rgba(255,255,255,0.06)',
    featuredBg: 'rgba(168,85,247,0.12)', featuredBorder: 'rgba(168,85,247,0.4)',
    tagBg: 'rgba(255,255,255,0.06)', tagBorder: 'rgba(255,255,255,0.1)', tagText: 'rgba(255,255,255,0.7)',
    divider: 'rgba(255,255,255,0.12)',
    badgeBg: 'rgba(255,255,255,0.05)', badgeBorder: 'rgba(255,255,255,0.2)', badgeText: 'rgba(255,255,255,0.6)',
    iconFill: 'white', logoFilter: 'brightness(0) invert(1)',
    flyerShadow: 'rgba(255,255,255,0.06)', flyerBorder: 'rgba(255,255,255,0.08)',
  },
  white: {
    label: 'White', swatch: '#fff',
    bg: '#ffffff',
    text: '#111', textMuted: 'rgba(0,0,0,0.55)', textSoft: 'rgba(0,0,0,0.4)', textFaint: 'rgba(0,0,0,0.25)',
    accent: '#a855f7',
    orbA: 'rgba(168,85,247,0.06)', orbB: 'rgba(168,85,247,0.04)',
    cardBg: 'rgba(0,0,0,0.02)', cardBorder: 'rgba(0,0,0,0.06)',
    featuredBg: 'rgba(168,85,247,0.06)', featuredBorder: 'rgba(168,85,247,0.35)',
    tagBg: 'rgba(0,0,0,0.04)', tagBorder: 'rgba(0,0,0,0.08)', tagText: 'rgba(0,0,0,0.6)',
    divider: 'rgba(0,0,0,0.1)',
    badgeBg: 'rgba(0,0,0,0.03)', badgeBorder: 'rgba(0,0,0,0.15)', badgeText: 'rgba(0,0,0,0.45)',
    iconFill: '#111', logoFilter: 'none',
    flyerShadow: 'rgba(0,0,0,0.06)', flyerBorder: 'rgba(0,0,0,0.08)',
  },
  purple: {
    label: 'Purple', swatch: '#7c3aed',
    bg: 'linear-gradient(170deg, #000000 0%, #0a0a0a 20%, #2d1040 50%, #1a0a2e 75%, #120822 100%)',
    text: '#fff', textMuted: 'rgba(255,255,255,0.6)', textSoft: 'rgba(255,255,255,0.45)', textFaint: 'rgba(255,255,255,0.3)',
    accent: '#c084fc',
    orbA: 'rgba(180,50,160,0.2)', orbB: 'rgba(160,40,200,0.15)',
    cardBg: 'rgba(255,255,255,0.04)', cardBorder: 'rgba(255,255,255,0.07)',
    featuredBg: 'rgba(192,132,252,0.15)', featuredBorder: 'rgba(192,132,252,0.45)',
    tagBg: 'rgba(255,255,255,0.06)', tagBorder: 'rgba(255,255,255,0.1)', tagText: 'rgba(255,255,255,0.7)',
    divider: 'rgba(255,255,255,0.15)',
    badgeBg: 'rgba(255,255,255,0.05)', badgeBorder: 'rgba(255,255,255,0.2)', badgeText: 'rgba(255,255,255,0.6)',
    iconFill: 'white', logoFilter: 'brightness(0) invert(1)',
    flyerShadow: 'rgba(255,255,255,0.06)', flyerBorder: 'rgba(255,255,255,0.08)',
  },
};

// ── Format configs ──
const FORMAT_CONFIGS = {
  story: { label: '9:16 Story', width: 1080, height: 1920, previewScale: 0.35 },
  post: { label: '1:1 Post', width: 1080, height: 1080, previewScale: 0.55 },
};

// ── Font options ──
const FONT_OPTIONS = [
  { key: 'inter', label: 'Inter', family: "'Inter', sans-serif", weights: '400;500;600;700;800;900', category: 'Clean' },
  { key: 'playfair', label: 'Playfair Display', family: "'Playfair Display', serif", weights: '400;500;600;700;800;900', category: 'Elegant' },
  { key: 'montserrat', label: 'Montserrat', family: "'Montserrat', sans-serif", weights: '400;500;600;700;800;900', category: 'Modern' },
  { key: 'space-grotesk', label: 'Space Grotesk', family: "'Space Grotesk', sans-serif", weights: '400;500;600;700', category: 'Tech' },
  { key: 'bebas-neue', label: 'Bebas Neue', family: "'Bebas Neue', sans-serif", weights: '400', category: 'Bold' },
  { key: 'outfit', label: 'Outfit', family: "'Outfit', sans-serif", weights: '400;500;600;700;800;900', category: 'Rounded' },
];

// ── Export quality ──
const EXPORT_QUALITY = [
  { key: 1, label: '1x' },
  { key: 2, label: '2x HD' },
  { key: 3, label: '3x Ultra' },
];

// ── Batch layouts ──
const BATCH_LAYOUTS = {
  cards: { label: 'Cards' },
  minimal: { label: 'Minimal' },
};

// ── Font loader ──
const loadedFonts = new Set(['inter']);
const loadGoogleFont = (fontOption) => {
  if (loadedFonts.has(fontOption.key)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${fontOption.label.replace(/ /g, '+')}:wght@${fontOption.weights}&display=swap`;
  document.head.appendChild(link);
  loadedFonts.add(fontOption.key);
};

// ── Theme resolver (merges accent override) ──
const resolveTheme = (themeKey, accentOverride) => {
  const base = THEMES[themeKey];
  if (!accentOverride) return base;
  return {
    ...base,
    accent: accentOverride,
    featuredBg: `${accentOverride}1F`,
    featuredBorder: `${accentOverride}66`,
  };
};

// ── Helpers ──
const extractEvent = (raw) => {
  const d = raw.publicationData || raw;
  return {
    _id: raw._id,
    title: d.title || 'Untitled',
    flyer: d.flyer || '',
    startTime: d.startTime,
    endTime: d.endTime,
    venue: d.organisationName || '',
    city: d.city?.value || d.city || d.localisation?.city?.value || d.localisation?.city || '',
    artists: d.tags || d.lineup || [],
  };
};

// ── Brand header ──
const StoryBrandHeader = ({ t, fontFamily, showLogo = true, customTitle, customSubtitle, compact }) => (
  <div style={{ textAlign: 'center', padding: compact ? '30px 0 0' : '50px 0 0' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: compact ? 10 : 16 }}>
      {showLogo && <img src={logoSway} alt="" style={{ height: compact ? 36 : 48, width: 'auto', filter: t.logoFilter }} />}
      <div style={{ fontSize: compact ? 28 : 38, fontWeight: 800, color: t.text, letterSpacing: 1, fontFamily }}>
        {customTitle || 'mysway.'}
      </div>
    </div>
    {customSubtitle && (
      <div style={{ fontSize: compact ? 18 : 22, fontWeight: 500, color: t.textMuted, marginTop: 6, fontFamily }}>
        {customSubtitle}
      </div>
    )}
  </div>
);

// ── App store badge SVGs ──
const AppleIcon = ({ fill }) => (
  <svg width="22" height="26" viewBox="0 0 22 26" fill={fill} xmlns="http://www.w3.org/2000/svg">
    <path d="M18.07 13.62c-.03-3.22 2.63-4.77 2.75-4.84-1.5-2.19-3.83-2.49-4.66-2.52-1.98-.2-3.87 1.17-4.88 1.17-1.01 0-2.56-1.14-4.21-1.11-2.17.03-4.17 1.26-5.29 3.2-2.25 3.91-.58 9.7 1.62 12.87 1.07 1.55 2.35 3.3 4.03 3.23 1.62-.06 2.23-1.05 4.18-1.05 1.96 0 2.5 1.05 4.21 1.02 1.74-.03 2.84-1.58 3.9-3.14 1.23-1.8 1.73-3.55 1.76-3.64-.04-.02-3.38-1.3-3.41-5.15zM14.87 3.94C15.75 2.87 16.35 1.4 16.19 0c-1.29.05-2.86.86-3.78 1.94-.83.96-1.55 2.49-1.36 3.96 1.44.11 2.91-.73 3.82-1.96z"/>
  </svg>
);

const PlayIcon = ({ fill }) => (
  <svg width="22" height="24" viewBox="0 0 22 24" fill={fill} xmlns="http://www.w3.org/2000/svg">
    <path d="M1.57.46C1.2.85 1 1.47 1 2.27v19.46c0 .8.2 1.42.57 1.81l.1.09L12.2 13.1v-.2L1.67.37l-.1.09z"/>
    <path d="M15.72 16.62l-3.52-3.52v-.2l3.52-3.52.08.05 4.17 2.37c1.19.68 1.19 1.79 0 2.46l-4.17 2.37-.08.05v-.06z"/>
    <path d="M15.8 16.57L12.2 12.97 1.57 23.54c.39.42 1.04.47 1.78.05L15.8 16.57z"/>
    <path d="M15.8 7.38L3.35.36C2.61-.06 1.96-.01 1.57.41L12.2 11.03l3.6-3.65z"/>
  </svg>
);

const StoryFooter = ({ t, showFooter = true, compact }) => {
  if (!showFooter) return null;
  return (
    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: compact ? '0 40px 20px' : '0 70px 40px', textAlign: 'center' }}>
      <div style={{ fontSize: compact ? 20 : 26, fontWeight: 700, color: t.text, marginBottom: compact ? 4 : 8 }}>
        Your City. Your Sound.
      </div>
      <div style={{ fontSize: compact ? 14 : 18, fontWeight: 400, color: t.textMuted, marginBottom: compact ? 12 : 20, lineHeight: 1.4 }}>
        Discover events, artists & nightlife near you. Free on iOS & Android.
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: compact ? 10 : 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: compact ? 6 : 10, padding: compact ? '6px 14px' : '10px 22px', borderRadius: 10, border: `1.5px solid ${t.badgeBorder}`, background: t.badgeBg }}>
          <AppleIcon fill={t.iconFill} />
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: compact ? 9 : 11, color: t.badgeText, lineHeight: 1 }}>Download on the</div>
            <div style={{ fontSize: compact ? 14 : 18, fontWeight: 600, color: t.text, lineHeight: 1.3 }}>App Store</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: compact ? 6 : 10, padding: compact ? '6px 14px' : '10px 22px', borderRadius: 10, border: `1.5px solid ${t.badgeBorder}`, background: t.badgeBg }}>
          <PlayIcon fill={t.iconFill} />
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: compact ? 9 : 11, color: t.badgeText, lineHeight: 1 }}>Get it on</div>
            <div style={{ fontSize: compact ? 14 : 18, fontWeight: 600, color: t.text, lineHeight: 1.3 }}>Google Play</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Single Event Story Template ──
const SingleStoryCanvas = React.forwardRef(({ event, theme, format = 'story', fontFamily, accentColor, showLogo, showFooter, customTitle, customSubtitle }, ref) => {
  if (!event) return null;
  const t = resolveTheme(theme, accentColor);
  const f = FORMAT_CONFIGS[format];
  const isPost = format === 'post';

  return (
    <div
      ref={ref}
      style={{
        width: f.width, height: f.height,
        background: t.bg,
        position: 'relative', overflow: 'hidden',
        fontFamily: fontFamily || "'Inter', sans-serif", color: t.text,
      }}
    >
      <div style={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, borderRadius: '50%', background: `radial-gradient(circle, ${t.orbA} 0%, transparent 70%)`, filter: 'blur(60px)' }} />
      <div style={{ position: 'absolute', bottom: 200, left: -80, width: 350, height: 350, borderRadius: '50%', background: `radial-gradient(circle, ${t.orbB} 0%, transparent 70%)`, filter: 'blur(50px)' }} />

      <StoryBrandHeader t={t} fontFamily={fontFamily} showLogo={showLogo} customTitle={customTitle} customSubtitle={customSubtitle} compact={isPost} />

      {isPost ? (
        /* ── 1:1 Post layout: side-by-side ── */
        <div style={{ display: 'flex', gap: 35, padding: '25px 50px 0', height: f.height - 280 }}>
          <div style={{ flexShrink: 0 }}>
            {event.flyer ? (
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', inset: -10, borderRadius: 22, background: t.flyerShadow, filter: 'blur(15px)' }} />
                <img src={event.flyer} alt="" crossOrigin="anonymous" style={{ width: 460, height: 460, objectFit: 'cover', borderRadius: 16, position: 'relative', zIndex: 1, border: `1px solid ${t.flyerBorder}` }} />
              </div>
            ) : (
              <div style={{ width: 460, height: 460, borderRadius: 16, background: t.cardBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 50, color: t.textFaint }}>No Flyer</div>
            )}
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', overflow: 'hidden' }}>
            <div style={{ fontSize: 38, fontWeight: 800, lineHeight: 1.15, marginBottom: 14 }}>{event.title}</div>
            {event.startTime && (
              <div style={{ fontSize: 22, fontWeight: 600, color: t.textMuted, marginBottom: 10 }}>
                {moment(event.startTime).format('dddd DD MMMM · HH:mm')}
                {event.endTime && ` — ${moment(event.endTime).format('HH:mm')}`}
              </div>
            )}
            {event.venue && <div style={{ fontSize: 20, fontWeight: 500, color: t.textSoft, marginBottom: 14 }}>{event.venue}</div>}
            {event.artists.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {event.artists.slice(0, 4).map((a, i) => (
                  <span key={i} style={{ padding: '5px 14px', borderRadius: 50, background: t.tagBg, border: `1px solid ${t.tagBorder}`, fontSize: 17, fontWeight: 500, color: t.tagText }}>{a}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* ── 9:16 Story layout: vertical ── */
        <>
          <div style={{ padding: '35px 60px 0', display: 'flex', justifyContent: 'center' }}>
            {event.flyer ? (
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', inset: -15, borderRadius: 28, background: t.flyerShadow, filter: 'blur(20px)' }} />
                <img src={event.flyer} alt="" crossOrigin="anonymous" style={{ width: 860, height: 860, objectFit: 'cover', borderRadius: 20, position: 'relative', zIndex: 1, border: `1px solid ${t.flyerBorder}` }} />
              </div>
            ) : (
              <div style={{ width: 860, height: 860, borderRadius: 20, background: t.cardBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 80, color: t.textFaint }}>No Flyer</div>
            )}
          </div>
          <div style={{ padding: '45px 70px 0' }}>
            <div style={{ fontSize: 50, fontWeight: 800, lineHeight: 1.15, marginBottom: 18 }}>{event.title}</div>
            {event.startTime && (
              <div style={{ fontSize: 30, fontWeight: 600, color: t.textMuted, marginBottom: 14 }}>
                {moment(event.startTime).format('dddd DD MMMM · HH:mm')}
                {event.endTime && ` — ${moment(event.endTime).format('HH:mm')}`}
              </div>
            )}
            {event.venue && <div style={{ fontSize: 28, fontWeight: 500, color: t.textSoft, marginBottom: 18 }}>{event.venue}</div>}
            {event.artists.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {event.artists.slice(0, 8).map((a, i) => (
                  <span key={i} style={{ padding: '7px 18px', borderRadius: 50, background: t.tagBg, border: `1px solid ${t.tagBorder}`, fontSize: 21, fontWeight: 500, color: t.tagText }}>{a}</span>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <StoryFooter t={t} showFooter={showFooter} compact={isPost} />
    </div>
  );
});

// ── Batch Story Template ──
const BatchStoryCanvas = React.forwardRef(({ events, dayLabel, cityName, theme, featuredIds, format = 'story', fontFamily, accentColor, showLogo, showFooter, customTitle, customSubtitle, batchLayout = 'cards', pageLabel }, ref) => {
  if (!events || events.length === 0) return null;
  const t = resolveTheme(theme, accentColor);
  const f = FORMAT_CONFIGS[format];
  const isPost = format === 'post';
  const isMinimal = batchLayout === 'minimal';

  const shown = events;
  const headerSpace = isPost ? 260 : 380;
  const footerSpace = isPost ? 130 : 220;
  const availableHeight = f.height - headerSpace - footerSpace;
  const cardHeight = Math.min(isPost ? 110 : 150, Math.floor(availableHeight / shown.length));

  return (
    <div
      ref={ref}
      style={{
        width: f.width, height: f.height,
        background: t.bg,
        position: 'relative', overflow: 'hidden',
        fontFamily: fontFamily || "'Inter', sans-serif", color: t.text,
      }}
    >
      <div style={{ position: 'absolute', top: -80, left: -80, width: 400, height: 400, borderRadius: '50%', background: `radial-gradient(circle, ${t.orbA} 0%, transparent 70%)`, filter: 'blur(60px)' }} />
      <div style={{ position: 'absolute', bottom: 100, right: -80, width: 350, height: 350, borderRadius: '50%', background: `radial-gradient(circle, ${t.orbB} 0%, transparent 70%)`, filter: 'blur(50px)' }} />

      <StoryBrandHeader t={t} fontFamily={fontFamily} showLogo={showLogo} customTitle={customTitle} customSubtitle={customSubtitle} compact={isPost} />

      <div style={{ padding: isPost ? '15px 50px 0' : '25px 70px 0', textAlign: 'center' }}>
        <div style={{ fontSize: isPost ? 38 : 52, fontWeight: 900, letterSpacing: 3, textTransform: 'uppercase' }}>
          {dayLabel}
        </div>
        {cityName && (
          <div style={{ fontSize: isPost ? 20 : 26, fontWeight: 600, color: t.accent, marginTop: 6, letterSpacing: 3, textTransform: 'uppercase' }}>
            {cityName}
          </div>
        )}
        <div style={{ width: 100, height: 2, background: t.divider, margin: `${isPost ? 10 : 18}px auto 0` }} />
      </div>

      <div style={{ padding: isPost ? '15px 40px 0' : '25px 50px 0' }}>
        {shown.map((ev, i) => {
          const isFeatured = featuredIds?.includes(ev._id);
          return (
            <div key={ev._id || i} style={{
              display: 'flex', alignItems: 'center', gap: isPost ? 16 : 22,
              padding: isPost ? '8px 14px' : '12px 18px',
              marginBottom: isPost ? 4 : 6,
              background: isFeatured ? t.featuredBg : t.cardBg,
              borderRadius: isPost ? 10 : 14,
              border: `1px solid ${isFeatured ? t.featuredBorder : t.cardBorder}`,
              height: isFeatured ? cardHeight + 6 : cardHeight,
              position: 'relative',
            }}>
              {isFeatured && (
                <div style={{ position: 'absolute', top: -1, right: 14, background: t.accent, color: '#fff', fontSize: isPost ? 10 : 13, fontWeight: 700, padding: isPost ? '2px 10px 3px' : '3px 14px 5px', borderRadius: '0 0 8px 8px', letterSpacing: 1, textTransform: 'uppercase' }}>
                  Featured
                </div>
              )}
              {!isMinimal && (
                ev.flyer ? (
                  <img src={ev.flyer} alt="" crossOrigin="anonymous" style={{ width: cardHeight - 24, height: cardHeight - 24, objectFit: 'cover', borderRadius: isPost ? 8 : 10, flexShrink: 0 }} />
                ) : (
                  <div style={{ width: cardHeight - 24, height: cardHeight - 24, borderRadius: isPost ? 8 : 10, background: t.cardBg, flexShrink: 0 }} />
                )
              )}
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <div style={{ fontSize: isPost ? (isFeatured ? 24 : 21) : (isFeatured ? 30 : 27), fontWeight: 700, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.title}</div>
                <div style={{ fontSize: isPost ? 17 : 21, color: t.textMuted, fontWeight: 500, marginTop: isPost ? 2 : 4 }}>
                  {ev.startTime ? moment(ev.startTime).format('HH:mm') : ''}
                  {ev.venue && <span>{' · '}{ev.venue}</span>}
                </div>
                {!isPost && ev.artists.length > 0 && (
                  <div style={{ fontSize: 17, color: t.textFaint, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ev.artists.slice(0, 4).join(' · ')}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {pageLabel && (
        <div style={{ position: 'absolute', bottom: showFooter ? (compact ? 115 : 185) : (compact ? 20 : 35), left: 0, right: 0, textAlign: 'center' }}>
          <span style={{ fontSize: isPost ? 16 : 20, fontWeight: 600, color: t.textFaint, background: t.cardBg, padding: '4px 16px', borderRadius: 20, border: `1px solid ${t.cardBorder}` }}>
            {pageLabel}
          </span>
        </div>
      )}

      <StoryFooter t={t} showFooter={showFooter} compact={isPost} />
    </div>
  );
});

// ── Theme Picker ──
const ThemePicker = ({ value, onChange }) => (
  <div className="flex items-center gap-2">
    <span className="text-xs font-medium text-gray-500">Theme</span>
    {Object.entries(THEMES).map(([key, t]) => (
      <div
        key={key}
        onClick={() => onChange(key)}
        title={t.label}
        style={{
          width: 32, height: 32, borderRadius: 8, cursor: 'pointer',
          background: t.swatch,
          border: value === key ? '2.5px solid #a855f7' : '2px solid #d1d5db',
          boxShadow: value === key ? '0 0 0 2px rgba(168,85,247,0.3)' : 'none',
          transition: 'all 0.15s',
        }}
      />
    ))}
  </div>
);

// ── Main Component ──
const MAX_PER_PAGE = { story: 10, post: 6 };

const EventStoryGenerator = () => {
  const singleRef = useRef(null);
  const batchPageRefs = useRef({});

  const [mode, setMode] = useState('single');
  const [theme, setTheme] = useState('dark');
  const [format, setFormat] = useState('story');
  const [currentPage, setCurrentPage] = useState(0);

  // Customization
  const [selectedFont, setSelectedFont] = useState('inter');
  const [accentColor, setAccentColor] = useState(null);
  const [titleOverride, setTitleOverride] = useState('');
  const [customSubtitle, setCustomSubtitle] = useState('');
  const [showLogo, setShowLogo] = useState(true);
  const [showFooter, setShowFooter] = useState(true);
  const [exportQuality, setExportQuality] = useState(1);
  const [batchLayout, setBatchLayout] = useState('cards');

  // Video
  const [videoGenerating, setVideoGenerating] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoType, setVideoType] = useState(null); // 'slides' | 'list'
  const videoFrameRefs = useRef({});
  const listVideoFrameRefs = useRef({});

  // Filters
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [dateRange, setDateRange] = useState([moment(), moment().add(14, 'days')]);
  const [countriesLoaded, setCountriesLoaded] = useState(false);

  // Events
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Selection
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const [featuredIds, setFeaturedIds] = useState([]);

  // Font
  const currentFontFamily = useMemo(() => {
    const font = FONT_OPTIONS.find(f => f.key === selectedFont);
    return font?.family || "'Inter', sans-serif";
  }, [selectedFont]);

  React.useEffect(() => {
    const font = FONT_OPTIONS.find(f => f.key === selectedFont);
    if (font) loadGoogleFont(font);
  }, [selectedFont]);

  React.useEffect(() => {
    if (countriesLoaded) return;
    (async () => {
      try {
        const res = await getActiveCountries();
        setCountries(res?.data?.data || res?.data || []);
      } catch { /* ignore */ }
      setCountriesLoaded(true);
    })();
  }, [countriesLoaded]);

  const handleCountryChange = (id) => {
    setSelectedCountry(id);
    setSelectedCity(null);
    setEvents([]);
    setSelectedEventId(null);
    setSelectedEventIds([]);
    setFeaturedIds([]);
    const country = countries.find(c => c._id === id);
    setCities((country?.cities || []).filter(c => c.isEnabled));
  };

  const handleLoadEvents = async () => {
    if (!selectedCity) { message.warning('Pick a city first'); return; }
    try {
      setLoading(true);
      setSelectedEventId(null);
      setSelectedEventIds([]);
      setFeaturedIds([]);
      const res = await fetchEventsForCalendar({
        city: selectedCity,
        country: selectedCountry,
        startDate: dateRange[0].toISOString(),
        endDate: dateRange[1].toISOString(),
      });
      setEvents(res?.data || []);
      if (!res?.data?.length) message.info('No events found');
    } catch { message.error('Failed to load events'); }
    finally { setLoading(false); }
  };

  const eventList = useMemo(() => events.map(extractEvent).sort((a, b) => new Date(a.startTime) - new Date(b.startTime)), [events]);

  const eventsByDay = useMemo(() => {
    const groups = {};
    eventList.forEach(ev => {
      if (!ev.startTime) return;
      const key = moment(ev.startTime).format('YYYY-MM-DD');
      if (!groups[key]) groups[key] = [];
      groups[key].push(ev);
    });
    return groups;
  }, [eventList]);

  const dayButtons = useMemo(() =>
    Object.keys(eventsByDay).sort().map(dateKey => ({
      dateKey,
      label: moment(dateKey).format('ddd DD MMM'),
      count: eventsByDay[dateKey].length,
      ids: eventsByDay[dateKey].map(e => e._id),
    }))
  , [eventsByDay]);

  const selectedEvent = useMemo(() => eventList.find(e => e._id === selectedEventId) || null, [selectedEventId, eventList]);
  const batchEvents = useMemo(() => eventList.filter(e => selectedEventIds.includes(e._id)), [selectedEventIds, eventList]);

  // Events for video (single or batch)
  const eventsForVideo = useMemo(() => {
    if (mode === 'single' && selectedEvent) return [selectedEvent];
    if (mode === 'batch' && batchEvents.length > 0) return batchEvents;
    return [];
  }, [mode, selectedEvent, batchEvents]);

  // Progressive list frames for list-reveal video (1 item, 2 items, 3 items...)
  const listVideoSteps = useMemo(() => {
    if (mode !== 'batch' || batchEvents.length === 0) return [];
    const limit = MAX_PER_PAGE[format] || 10;
    const evs = batchEvents.slice(0, limit);
    return evs.map((_, idx) => evs.slice(0, idx + 1));
  }, [mode, batchEvents, format]);

  // Paginate batch events
  const perPage = MAX_PER_PAGE[format] || 10;
  const batchPages = useMemo(() => {
    if (batchEvents.length === 0) return [];
    const pages = [];
    for (let i = 0; i < batchEvents.length; i += perPage) {
      pages.push(batchEvents.slice(i, i + perPage));
    }
    return pages;
  }, [batchEvents, perPage]);

  const totalPages = batchPages.length;

  // Reset page when selection or format changes
  React.useEffect(() => {
    setCurrentPage(0);
  }, [selectedEventIds, format]);

  const batchDayLabel = useMemo(() => {
    if (batchEvents.length === 0) return '';
    const dates = [...new Set(batchEvents.map(e => e.startTime ? moment(e.startTime).format('YYYY-MM-DD') : null).filter(Boolean))];
    if (dates.length === 1) return moment(dates[0]).format('dddd DD MMMM');
    const d1 = moment(dates[0]);
    const d2 = moment(dates[dates.length - 1]);
    if (dates.length === 2 && ((d1.day() === 5 && d2.day() === 6) || (d1.day() === 6 && d2.day() === 0))) return 'This Weekend';
    return `${d1.format('DD MMM')} — ${d2.format('DD MMM')}`;
  }, [batchEvents]);

  const cityName = useMemo(() => {
    const country = countries.find(c => c._id === selectedCountry);
    return country?.cities?.find(c => c._id === selectedCity)?.name || '';
  }, [selectedCountry, selectedCity, countries]);

  const downloadRef = useCallback(async (ref, filename) => {
    if (!ref?.current) return;
    await document.fonts.ready;
    const fmtCfg = FORMAT_CONFIGS[format];
    const dataUrl = await toPng(ref.current, { width: fmtCfg.width, height: fmtCfg.height, pixelRatio: exportQuality, cacheBust: true });
    const link = document.createElement('a');
    link.download = filename;
    link.href = dataUrl;
    link.click();
  }, [format, exportQuality]);

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const prefix = format === 'post' ? 'post' : 'story';
      if (mode === 'single') {
        if (!singleRef.current || !selectedEvent) return;
        const slug = selectedEvent.title.replace(/[^a-z0-9]/gi, '_').substring(0, 30);
        await downloadRef(singleRef, `${prefix}-${slug}.png`);
      } else {
        if (totalPages === 0) return;
        const slug = batchDayLabel.replace(/[^a-z0-9]/gi, '_').substring(0, 30);
        if (totalPages === 1) {
          await downloadRef({ current: batchPageRefs.current[0] }, `${prefix}-${slug}.png`);
        } else {
          for (let i = 0; i < totalPages; i++) {
            const ref = { current: batchPageRefs.current[i] };
            if (!ref.current) continue;
            await downloadRef(ref, `${prefix}-${slug}-p${i + 1}.png`);
            if (i < totalPages - 1) await new Promise(r => setTimeout(r, 300));
          }
        }
      }
      message.success(`${FORMAT_CONFIGS[format].label} downloaded at ${exportQuality}x!`);
    } catch (err) {
      console.error(err);
      message.error('Failed to generate image');
    } finally { setDownloading(false); }
  };

  const toggleEvent = (id) => setSelectedEventIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleFeatured = (id) => setFeaturedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // ── Video Generation ──
  const handleGenerateVideo = async () => {
    const events = eventsForVideo;
    if (events.length === 0) { message.warning('Select events first'); return; }
    if (typeof VideoEncoder === 'undefined') {
      message.error('Video encoding not supported. Use Chrome 94+ or Edge 94+.');
      return;
    }

    setVideoGenerating(true);
    setVideoType('slides');
    setVideoProgress(0);

    try {
      const fmtCfg = FORMAT_CONFIGS[format];
      const { width, height } = fmtCfg;
      const fps = 30;
      const holdSeconds = 3;
      const fadeSeconds = 0.5;
      const holdFrames = Math.round(fps * holdSeconds);
      const fadeFrames = Math.round(fps * fadeSeconds);

      // 1. Pre-render each event as PNG
      await document.fonts.ready;
      const images = [];
      for (let i = 0; i < events.length; i++) {
        const el = videoFrameRefs.current[i];
        if (!el) continue;
        const dataUrl = await toPng(el, { width, height, pixelRatio: 1, cacheBust: true });
        const img = new Image();
        img.src = dataUrl;
        await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; });
        images.push(img);
        setVideoProgress(Math.round(((i + 1) / events.length) * 25));
      }
      if (images.length === 0) throw new Error('No frames captured');

      // 2. Set up off-screen canvas
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      // 3. Set up mp4-muxer
      const target = new ArrayBufferTarget();
      const muxer = new Muxer({
        target,
        video: { codec: 'avc', width, height, frameRate: fps },
        fastStart: 'in-memory',
      });

      // 4. Configure VideoEncoder (H.264 High Profile)
      let codecString = 'avc1.640028'; // Level 4.0
      let config = { codec: codecString, width, height, bitrate: 4_000_000, framerate: fps };
      let { supported } = await VideoEncoder.isConfigSupported(config);
      if (!supported) {
        codecString = 'avc1.640033'; // Level 5.1
        config = { ...config, codec: codecString };
        const res2 = await VideoEncoder.isConfigSupported(config);
        if (!res2.supported) throw new Error('H.264 encoding not supported at this resolution');
      }

      const encoder = new VideoEncoder({
        output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
        error: (e) => console.error('VideoEncoder error:', e),
      });
      encoder.configure(config);

      // 5. Render frames with Ken Burns zoom + crossfade
      let frameCount = 0;
      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        const nextImg = images[i + 1] || null;

        // Hold phase — subtle zoom from 1.0→1.03
        for (let f = 0; f < holdFrames; f++) {
          const zoom = 1 + 0.03 * (f / holdFrames);
          const ox = (width * (zoom - 1)) / 2;
          const oy = (height * (zoom - 1)) / 2;
          ctx.globalAlpha = 1;
          ctx.clearRect(0, 0, width, height);
          ctx.drawImage(img, -ox, -oy, width * zoom, height * zoom);

          const vf = new VideoFrame(canvas, { timestamp: Math.round((frameCount * 1_000_000) / fps) });
          encoder.encode(vf, { keyFrame: frameCount % (fps * 2) === 0 });
          vf.close();
          frameCount++;
        }

        // Crossfade to next event, or fade to black on last
        for (let f = 0; f < fadeFrames; f++) {
          const progress = f / fadeFrames;
          ctx.clearRect(0, 0, width, height);
          ctx.globalAlpha = 1;
          ctx.drawImage(img, -(width * 0.03) / 2, -(height * 0.03) / 2, width * 1.03, height * 1.03);
          if (nextImg) {
            ctx.globalAlpha = progress;
            ctx.drawImage(nextImg, 0, 0, width, height);
          } else {
            ctx.globalAlpha = progress;
            ctx.fillStyle = '#000';
            ctx.fillRect(0, 0, width, height);
          }
          ctx.globalAlpha = 1;

          const vf = new VideoFrame(canvas, { timestamp: Math.round((frameCount * 1_000_000) / fps) });
          encoder.encode(vf, { keyFrame: false });
          vf.close();
          frameCount++;
        }

        setVideoProgress(25 + Math.round(((i + 1) / images.length) * 65));
      }

      // 6. Flush encoder & finalize MP4
      setVideoProgress(92);
      await encoder.flush();
      encoder.close();
      muxer.finalize();

      // 7. Download MP4
      const blob = new Blob([target.buffer], { type: 'video/mp4' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.download = `mysway-${format}-${events.length}events.mp4`;
      a.href = url;
      a.click();
      URL.revokeObjectURL(url);

      setVideoProgress(100);
      message.success(`MP4 video exported — ${events.length} events!`);
    } catch (err) {
      console.error('Video generation failed:', err);
      message.error(`Video generation failed: ${err.message}`);
    } finally {
      setVideoGenerating(false);
      setVideoType(null);
    }
  };

  // ── List Reveal Video — items appear one by one on the batch canvas ──
  const handleGenerateListVideo = async () => {
    if (listVideoSteps.length === 0) { message.warning('Select events in batch mode first'); return; }
    if (typeof VideoEncoder === 'undefined') {
      message.error('Video encoding not supported. Use Chrome 94+ or Edge 94+.');
      return;
    }

    setVideoGenerating(true);
    setVideoType('list');
    setVideoProgress(0);

    try {
      const fmtCfg = FORMAT_CONFIGS[format];
      const { width, height } = fmtCfg;
      const fps = 30;
      const holdSeconds = 2;
      const fadeSeconds = 0.3;
      const lastHoldSeconds = 3;
      const holdFrames = Math.round(fps * holdSeconds);
      const fadeFrames = Math.round(fps * fadeSeconds);
      const lastHoldFrames = Math.round(fps * lastHoldSeconds);
      const steps = listVideoSteps.length;

      // 1. Pre-render each progressive step as PNG
      await document.fonts.ready;
      const images = [];
      for (let i = 0; i < steps; i++) {
        const el = listVideoFrameRefs.current[i];
        if (!el) continue;
        const dataUrl = await toPng(el, { width, height, pixelRatio: 1, cacheBust: true });
        const img = new Image();
        img.src = dataUrl;
        await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; });
        images.push(img);
        setVideoProgress(Math.round(((i + 1) / steps) * 25));
      }
      if (images.length === 0) throw new Error('No frames captured');

      // 2. Canvas + muxer + encoder setup
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      const target = new ArrayBufferTarget();
      const muxer = new Muxer({
        target,
        video: { codec: 'avc', width, height, frameRate: fps },
        fastStart: 'in-memory',
      });

      let codecString = 'avc1.640028';
      let config = { codec: codecString, width, height, bitrate: 4_000_000, framerate: fps };
      let { supported } = await VideoEncoder.isConfigSupported(config);
      if (!supported) {
        codecString = 'avc1.640033';
        config = { ...config, codec: codecString };
        const res2 = await VideoEncoder.isConfigSupported(config);
        if (!res2.supported) throw new Error('H.264 encoding not supported at this resolution');
      }

      const encoder = new VideoEncoder({
        output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
        error: (e) => console.error('VideoEncoder error:', e),
      });
      encoder.configure(config);

      // 3. Render frames — each step holds then crossfades to reveal next item
      let frameCount = 0;
      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        const nextImg = images[i + 1] || null;
        const isLast = i === images.length - 1;
        const frames = isLast ? lastHoldFrames : holdFrames;

        // Hold phase
        for (let f = 0; f < frames; f++) {
          ctx.globalAlpha = 1;
          ctx.clearRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);

          const vf = new VideoFrame(canvas, { timestamp: Math.round((frameCount * 1_000_000) / fps) });
          encoder.encode(vf, { keyFrame: frameCount % (fps * 2) === 0 });
          vf.close();
          frameCount++;
        }

        // Crossfade to next step (one more item) or fade to black
        const fadeTo = nextImg || null;
        for (let f = 0; f < fadeFrames; f++) {
          const progress = f / fadeFrames;
          ctx.clearRect(0, 0, width, height);
          ctx.globalAlpha = 1;
          ctx.drawImage(img, 0, 0, width, height);
          if (fadeTo) {
            ctx.globalAlpha = progress;
            ctx.drawImage(fadeTo, 0, 0, width, height);
          } else {
            ctx.globalAlpha = progress;
            ctx.fillStyle = '#000';
            ctx.fillRect(0, 0, width, height);
          }
          ctx.globalAlpha = 1;

          const vf = new VideoFrame(canvas, { timestamp: Math.round((frameCount * 1_000_000) / fps) });
          encoder.encode(vf, { keyFrame: false });
          vf.close();
          frameCount++;
        }

        setVideoProgress(25 + Math.round(((i + 1) / images.length) * 65));
      }

      // 4. Finalize
      setVideoProgress(92);
      await encoder.flush();
      encoder.close();
      muxer.finalize();

      const blob = new Blob([target.buffer], { type: 'video/mp4' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.download = `mysway-${format}-list-reveal.mp4`;
      a.href = url;
      a.click();
      URL.revokeObjectURL(url);

      setVideoProgress(100);
      message.success(`List reveal video exported — ${images.length} steps!`);
    } catch (err) {
      console.error('List video generation failed:', err);
      message.error(`List video failed: ${err.message}`);
    } finally {
      setVideoGenerating(false);
      setVideoType(null);
    }
  };

  const canvasProps = {
    theme, format,
    fontFamily: currentFontFamily,
    accentColor,
    showLogo,
    showFooter,
    customTitle: titleOverride || undefined,
    customSubtitle: customSubtitle || undefined,
  };

  const fc = FORMAT_CONFIGS[format];

  return (
    <Card
      title={<span><CameraOutlined className="mr-2" />Instagram Content Generator</span>}
      className="shadow-lg mt-6"
    >
      {/* ── Toolbar Row 1: Mode + Format + Theme ── */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <Segmented
          value={mode}
          onChange={(v) => { setMode(v); setSelectedEventId(null); setSelectedEventIds([]); setFeaturedIds([]); }}
          options={[
            { label: <span><PictureOutlined /> Single</span>, value: 'single' },
            { label: <span><UnorderedListOutlined /> Batch</span>, value: 'batch' },
          ]}
          size="large"
        />
        <Segmented
          value={format}
          onChange={setFormat}
          options={[
            { label: <span><PictureOutlined /> 9:16 Story</span>, value: 'story' },
            { label: <span><AppstoreOutlined /> 1:1 Post</span>, value: 'post' },
          ]}
          size="large"
        />
        <ThemePicker value={theme} onChange={setTheme} />
      </div>

      {/* ── Toolbar Row 2: Filters ── */}
      <div className="flex flex-wrap items-end gap-3 mb-3">
        <div>
          <div className="text-xs font-medium text-gray-500 mb-1">Country</div>
          <Select value={selectedCountry} onChange={handleCountryChange} style={{ width: 170 }} placeholder="Country" showSearch optionFilterProp="children">
            {countries.map(c => <Option key={c._id} value={c._id}>{c.name}</Option>)}
          </Select>
        </div>
        <div>
          <div className="text-xs font-medium text-gray-500 mb-1">City</div>
          <Select value={selectedCity} onChange={setSelectedCity} style={{ width: 170 }} placeholder="City" disabled={!selectedCountry} showSearch optionFilterProp="children">
            {cities.map(c => <Option key={c._id} value={c._id}>{c.name}</Option>)}
          </Select>
        </div>
        <div>
          <div className="text-xs font-medium text-gray-500 mb-1">Date Range</div>
          <RangePicker value={dateRange} onChange={(d) => d && setDateRange(d)} />
        </div>
        <Button type="primary" icon={<ReloadOutlined />} loading={loading} onClick={handleLoadEvents} disabled={!selectedCity}>
          Load Events
        </Button>
      </div>

      {/* ── Customize Panel ── */}
      <Collapse
        ghost
        style={{ marginBottom: 12 }}
        items={[{
          key: 'customize',
          label: <span className="text-sm"><SettingOutlined className="mr-1" />Customize</span>,
          children: (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-2">
              <div>
                <div className="text-xs font-medium text-gray-500 mb-1"><FontSizeOutlined /> Font</div>
                <Select value={selectedFont} onChange={setSelectedFont} style={{ width: '100%' }} optionLabelProp="label">
                  {FONT_OPTIONS.map(f => (
                    <Option key={f.key} value={f.key} label={f.label}>
                      <div className="flex justify-between items-center">
                        <span style={{ fontFamily: f.family }}>{f.label}</span>
                        <span className="text-xs text-gray-400">{f.category}</span>
                      </div>
                    </Option>
                  ))}
                </Select>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-500 mb-1"><BgColorsOutlined /> Accent Color</div>
                <div className="flex items-center gap-2">
                  <ColorPicker
                    value={accentColor || THEMES[theme].accent}
                    onChange={(_, hex) => setAccentColor(hex)}
                    size="small"
                  />
                  {accentColor && <Button size="small" type="link" onClick={() => setAccentColor(null)}>Reset</Button>}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-500 mb-1">Brand Title</div>
                <Input value={titleOverride} onChange={(e) => setTitleOverride(e.target.value)} placeholder="mysway." size="small" allowClear />
              </div>
              <div>
                <div className="text-xs font-medium text-gray-500 mb-1">Subtitle</div>
                <Input value={customSubtitle} onChange={(e) => setCustomSubtitle(e.target.value)} placeholder="e.g. Presented by..." size="small" allowClear />
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <Switch checked={showLogo} onChange={setShowLogo} size="small" />
                  <span className="text-xs text-gray-600">Logo</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Switch checked={showFooter} onChange={setShowFooter} size="small" />
                  <span className="text-xs text-gray-600">Footer</span>
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-500 mb-1">Export Quality</div>
                <Segmented value={exportQuality} onChange={setExportQuality} options={EXPORT_QUALITY.map(q => ({ label: q.label, value: q.key }))} size="small" />
              </div>
              {mode === 'batch' && (
                <div>
                  <div className="text-xs font-medium text-gray-500 mb-1">Batch Layout</div>
                  <Segmented value={batchLayout} onChange={setBatchLayout} options={Object.entries(BATCH_LAYOUTS).map(([k, v]) => ({ label: v.label, value: k }))} size="small" />
                </div>
              )}
            </div>
          ),
        }]}
      />

      {loading && <div className="text-center py-8"><Spin size="large" /></div>}

      {!loading && events.length > 0 && (
        <div className="flex gap-6" style={{ minHeight: 600 }}>
          {/* Left: Event list */}
          <div style={{ width: 380, flexShrink: 0, maxHeight: 720, overflowY: 'auto' }} className="pr-2">
            {mode === 'batch' && dayButtons.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {dayButtons.map(d => (
                  <Tag
                    key={d.dateKey}
                    color={selectedEventIds.length > 0 && d.ids.every(id => selectedEventIds.includes(id)) ? 'blue' : 'default'}
                    className="cursor-pointer"
                    style={{ fontSize: 13, padding: '2px 10px' }}
                    onClick={() => {
                      const allSelected = d.ids.every(id => selectedEventIds.includes(id));
                      if (allSelected) {
                        setSelectedEventIds(prev => prev.filter(id => !d.ids.includes(id)));
                      } else {
                        setSelectedEventIds(prev => [...new Set([...prev, ...d.ids])]);
                      }
                    }}
                  >
                    {d.label} ({d.count})
                  </Tag>
                ))}
                {selectedEventIds.length > 0 && (
                  <Tag color="red" className="cursor-pointer" style={{ fontSize: 13, padding: '2px 10px' }} onClick={() => { setSelectedEventIds([]); setFeaturedIds([]); }}>
                    Clear
                  </Tag>
                )}
              </div>
            )}

            <div className="text-sm font-semibold text-gray-600 mb-2">
              {eventList.length} Events
              {mode === 'batch' && selectedEventIds.length > 0 && <span className="text-blue-500 ml-2">({selectedEventIds.length} selected)</span>}
              {mode === 'batch' && featuredIds.length > 0 && <span className="text-purple-500 ml-2">({featuredIds.length} featured)</span>}
            </div>

            {mode === 'single' ? (
              <Radio.Group value={selectedEventId} onChange={(e) => setSelectedEventId(e.target.value)} className="w-full">
                <Space direction="vertical" className="w-full">
                  {eventList.map(ev => (
                    <Radio key={ev._id} value={ev._id} className="w-full">
                      <EventListItem ev={ev} />
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
            ) : (
              <Space direction="vertical" className="w-full">
                {eventList.map(ev => (
                  <div key={ev._id} className="flex items-center gap-1">
                    <Checkbox checked={selectedEventIds.includes(ev._id)} onChange={() => toggleEvent(ev._id)}>
                      <EventListItem ev={ev} />
                    </Checkbox>
                    {selectedEventIds.includes(ev._id) && (
                      <Tooltip title={featuredIds.includes(ev._id) ? 'Remove featured' : 'Mark as featured'}>
                        <Button
                          type="text" size="small"
                          onClick={() => toggleFeatured(ev._id)}
                          style={{ color: featuredIds.includes(ev._id) ? '#a855f7' : '#ccc', padding: '0 4px' }}
                          icon={featuredIds.includes(ev._id) ? <StarFilled /> : <StarOutlined />}
                        />
                      </Tooltip>
                    )}
                  </div>
                ))}
              </Space>
            )}
          </div>

          {/* Right: Story preview */}
          <div className="flex-1 flex flex-col items-center">
            {mode === 'single' && selectedEvent && (
              <>
                <div style={{ transform: `scale(${fc.previewScale})`, transformOrigin: 'top center', width: fc.width, height: fc.height, marginBottom: -(fc.height * (1 - fc.previewScale)) }}>
                  <SingleStoryCanvas ref={singleRef} event={selectedEvent} {...canvasProps} />
                </div>
                <div className="flex gap-3" style={{ marginTop: 16 }}>
                  <Button type="primary" size="large" icon={<DownloadOutlined />} loading={downloading} onClick={handleDownload}>
                    Download {fc.label}
                  </Button>
                  <Button size="large" icon={<VideoCameraOutlined />} loading={videoGenerating && videoType === 'slides'} disabled={videoGenerating} onClick={handleGenerateVideo}>
                    Generate Video
                  </Button>
                </div>
                {videoGenerating && (
                  <div style={{ width: 300, marginTop: 12 }}>
                    <Progress percent={videoProgress} size="small" status="active" format={(p) => `${p}%`} />
                    <div className="text-xs text-gray-400 text-center mt-1">
                      {videoProgress < 25 ? 'Rendering frames...' : videoProgress < 90 ? 'Encoding video...' : 'Finalizing MP4...'}
                    </div>
                  </div>
                )}
              </>
            )}

            {mode === 'batch' && batchPages.length > 0 && (
              <>
                {/* Render all pages for download capture — only current page visible */}
                <div style={{ position: 'relative' }}>
                  {batchPages.map((pageEvents, idx) => (
                    <div
                      key={idx}
                      style={{
                        transform: `scale(${fc.previewScale})`, transformOrigin: 'top center',
                        width: fc.width, height: fc.height,
                        marginBottom: -(fc.height * (1 - fc.previewScale)),
                        display: idx === currentPage ? 'block' : 'none',
                      }}
                    >
                      <BatchStoryCanvas
                        ref={el => { batchPageRefs.current[idx] = el; }}
                        events={pageEvents}
                        dayLabel={batchDayLabel}
                        cityName={cityName}
                        featuredIds={featuredIds}
                        batchLayout={batchLayout}
                        pageLabel={totalPages > 1 ? `${idx + 1} / ${totalPages}` : undefined}
                        {...canvasProps}
                      />
                    </div>
                  ))}
                  {/* Hidden pages for download capture */}
                  {batchPages.map((pageEvents, idx) => idx !== currentPage && (
                    <div key={`hidden-${idx}`} style={{ position: 'absolute', left: -9999, top: 0 }}>
                      <BatchStoryCanvas
                        ref={el => { batchPageRefs.current[idx] = el; }}
                        events={pageEvents}
                        dayLabel={batchDayLabel}
                        cityName={cityName}
                        featuredIds={featuredIds}
                        batchLayout={batchLayout}
                        pageLabel={totalPages > 1 ? `${idx + 1} / ${totalPages}` : undefined}
                        {...canvasProps}
                      />
                    </div>
                  ))}
                </div>

                {/* Page navigation */}
                {totalPages > 1 && (
                  <div className="flex items-center gap-3" style={{ marginTop: 12 }}>
                    <Button icon={<LeftOutlined />} disabled={currentPage === 0} onClick={() => setCurrentPage(p => p - 1)} />
                    <span className="text-sm font-medium text-gray-600">Page {currentPage + 1} of {totalPages}</span>
                    <Button icon={<RightOutlined />} disabled={currentPage >= totalPages - 1} onClick={() => setCurrentPage(p => p + 1)} />
                  </div>
                )}

                <div className="flex flex-wrap gap-3" style={{ marginTop: 12 }}>
                  <Button type="primary" size="large" icon={<DownloadOutlined />} loading={downloading} onClick={handleDownload}>
                    {totalPages > 1 ? `Download All ${totalPages} Pages` : `Download ${fc.label}`}
                  </Button>
                  <Button size="large" icon={<VideoCameraOutlined />} loading={videoGenerating && videoType === 'slides'} disabled={videoGenerating} onClick={handleGenerateVideo}>
                    Slides Video
                  </Button>
                  <Button size="large" icon={<UnorderedListOutlined />} loading={videoGenerating && videoType === 'list'} disabled={videoGenerating} onClick={handleGenerateListVideo}>
                    List Reveal
                  </Button>
                </div>
                {videoGenerating && (
                  <div style={{ width: 300, marginTop: 12 }}>
                    <Progress percent={videoProgress} size="small" status="active" format={(p) => `${p}%`} />
                    <div className="text-xs text-gray-400 text-center mt-1">
                      {videoProgress < 25 ? 'Rendering frames...' : videoProgress < 90 ? 'Encoding video...' : 'Finalizing MP4...'}
                    </div>
                  </div>
                )}
              </>
            )}

            {((mode === 'single' && !selectedEvent) || (mode === 'batch' && batchEvents.length === 0)) && (
              <div className="flex items-center justify-center h-full text-gray-400">
                {mode === 'single' ? 'Select an event to preview' : 'Select events or click a day tag'}
              </div>
            )}
          </div>
        </div>
      )}

      {!loading && events.length === 0 && countriesLoaded && (
        <Empty description="Load events from a city to get started" />
      )}

      {/* Hidden video frames for slides capture */}
      {eventsForVideo.length > 0 && (
        <div style={{ position: 'absolute', left: -99999, top: 0, pointerEvents: 'none' }}>
          {eventsForVideo.map((ev, idx) => (
            <SingleStoryCanvas
              key={ev._id}
              ref={el => { videoFrameRefs.current[idx] = el; }}
              event={ev}
              {...canvasProps}
            />
          ))}
        </div>
      )}

      {/* Hidden progressive list frames for list-reveal capture */}
      {listVideoSteps.length > 0 && (
        <div style={{ position: 'absolute', left: -99999, top: 0, pointerEvents: 'none' }}>
          {listVideoSteps.map((stepEvents, idx) => (
            <BatchStoryCanvas
              key={idx}
              ref={el => { listVideoFrameRefs.current[idx] = el; }}
              events={stepEvents}
              dayLabel={batchDayLabel}
              cityName={cityName}
              featuredIds={featuredIds}
              batchLayout={batchLayout}
              {...canvasProps}
            />
          ))}
        </div>
      )}
    </Card>
  );
};

const EventListItem = ({ ev }) => (
  <div className="flex items-center gap-2 py-1">
    {ev.flyer ? (
      <img src={ev.flyer} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
    ) : (
      <div className="w-10 h-10 rounded bg-gray-200 flex-shrink-0" />
    )}
    <div className="min-w-0">
      <div className="text-sm font-medium truncate" style={{ maxWidth: 220 }}>{ev.title}</div>
      <div className="text-xs text-gray-400">
        {ev.startTime ? moment(ev.startTime).format('ddd DD MMM · HH:mm') : '—'}
        {ev.venue && ` · ${ev.venue}`}
      </div>
    </div>
  </div>
);

export default EventStoryGenerator;
